import { Component, input, signal, effect, inject, output } from "@angular/core";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { WkTranslatePipe } from "@shared/utils";
import type {
    WkDataTableToolbarItems,
    WkDatableData,
    WkDataTableActions,
    WkDataTableInternalData,
    WkDataTableInternalDataItem,
    WkDatatableRowFields,
    WkDataTableFieldName,
    WkDataTableCellValue,
    WkDataTableSorting,
    WkDataTableColumns,
} from "./data-table.types";
import {
    WkDataTableToolbar,
    WkDataTableSearchbar,
    WkDataTableActionbar,
    WkDataTableVirtual,
    WkDataTablePaging,
    WkDataTableDraggingService,
    WkDataTableItemsService,
    WkDataTableSearchService,
    WkDataTableSelectionService,
} from "./data-table.components";
import { WkDataTableContextService } from "@components/data-table/data-table-context.service";
import { WkDataTableStoreService } from "./store";
import { FilterService } from "@filter";

@Component({
    selector: "wk-data-table",
    templateUrl: "./data-table.component.html",
    styleUrl: "./data-table.component.scss",
    host: { class: "wk-data-table", "[class.wk-data-table--virtual]": "virtual()" },
    imports: [
        JumpstartComponentsModule,
        WkTranslatePipe,
        WkDataTableToolbar,
        WkDataTableSearchbar,
        WkDataTableActionbar,
        WkDataTableVirtual,
        WkDataTablePaging,
    ],
    providers: [WkDataTableSelectionService, WkDataTableDraggingService, WkDataTableItemsService],
})
export class WkDataTableComponent<TItem> {
    /**
     * Injections
     */
    private readonly dataTableStore = inject(WkDataTableStoreService);
    private readonly dataTableSearchService = inject<WkDataTableSearchService<TItem>>(WkDataTableSearchService);
    private readonly dataTableContextService = inject<WkDataTableContextService<TItem>>(WkDataTableContextService);
    private readonly dataTableItemsService = inject<WkDataTableItemsService<TItem>>(WkDataTableItemsService);
    private readonly filterService = inject(FilterService);

    /**
     * Component Properties
     */

    public title = input<string>("");
    public toolbar = input<WkDataTableToolbarItems>([]);
    public searchable = input<boolean>(true);
    public primaryField = input.required<keyof TItem & string>();
    public data = input.required<WkDatableData<TItem>>();
    public selectable = input<boolean>(true);
    public showResultSummary = input<boolean>(true);
    public pageable = input<boolean>(true);
    public editable = input<boolean>(false);
    public actions = input<WkDataTableActions<TItem>>([]);
    public virtual = input<boolean>(true);
    public enableRowClick = input<boolean>(false);
    public sorting = input<WkDataTableSorting>();
    public columns = input.required<WkDataTableColumns>();
    public reorderableColumns = input<boolean>(true);

    /**
     * Component Outputs
     */

    public onRowClick = output<WkDataTableInternalDataItem<TItem>>();
    public onSave = output<WkDatableData<TItem>>();

    constructor() {
        /**
         * Columns
         */
        effect(() => {
            const columns = this.columns();
            this.dataTableStore.setColumns(columns);
        });

        /**
         * Context Management
         */
        this.dataTableContextService.setContext({
            enableRowClick: this.enableRowClick,
            onRowClick: this.onRowClick,
            selectable: this.selectable,
            data: this._data,
            sorting: this.sorting,
            primaryField: this.primaryField as never,
            actions: this.actions,
            reorderableColumns: this.reorderableColumns,
        });

        /**
         * Data Management
         */

        // Initialize internal data when input data changes
        effect(() => {
            const data = this.filterService.applyFilter(this.data)();
            const visibleColumns = this.dataTableStore.visibleColumns();
            const searchableColumns = this.dataTableSearchService.columns();
            const editable = this.editable();

            const _data = data.map((item) => {
                const fields: WkDatatableRowFields = {} as WkDatatableRowFields;

                if (editable) {
                    for (const column of visibleColumns) {
                        if (column.definition.input) fields[column.fullFieldName()] = column.getInputValue()?.(item);
                    }
                }

                const $searchCache = searchableColumns.map((k) => ({
                    value: this.dataTableSearchService.normalize(String(k.getStringValue()?.(item) ?? "")),
                    exactMatch: k.definition.exactMatch ?? false,
                }));

                const vmItem: WkDataTableInternalDataItem<TItem> = {
                    value: item,
                    fields,
                    $selected: false,
                    $edited: false,
                    $searchCache,
                };

                return {
                    ...vmItem,
                    updateField: (fieldname: WkDataTableFieldName, value: WkDataTableCellValue) => this.updateField(vmItem, fieldname, value),
                    markEdited: () => this.markEdited(vmItem),
                };
            });

            this._data.set(_data);
        });
    }

    /**
     * Data Management
     */
    protected _data = signal<WkDataTableInternalData<TItem>>([]);

    /**
     * Form Management
     */

    protected edited = signal<boolean>(false);

    private updateField(item: WkDataTableInternalDataItem<TItem>, fieldname: WkDataTableFieldName, value: WkDataTableCellValue) {
        if (!item) return;
        item.fields[fieldname] = value;
        item.$edited = true;
        this.edited.set(true);
    }

    private markEdited(item: WkDataTableInternalDataItem<TItem>) {
        if (!item) return;
        item.$edited = true;
        this.edited.set(true);
    }

    protected saveDataTable() {
        const edited = this.edited();
        if (!edited) return;

        const updated = this.dataTableItemsService.items().map((vm) => ({
            ...vm.value,
            ...vm.fields,
        }));

        this.onSave.emit(updated);
    }

    protected cancelDataTable() {
        console.log("Cancelled changes");
    }
}
